import { type Observable } from 'rxjs';

import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import type { ApiResponse } from '@shared/http/types';

import type { CredentialsDto, RegistrationInfoDto } from './dto';
import {
  mapCredentialsFromDto,
  mapRegistrationInfoFromDto,
  mapCompleteRegistrationPayloadToDto,
  mapCompletePasswordResetPayloadToDto,
} from './mappers';
import type {
  Credentials,
  LoginPayload,
  RegistrationInfo,
  InitPasswordResetPayload,
  CompleteRegistrationPayload,
  CompletePasswordResetPayload,
} from './models';

import { mapDataNonNull } from '@shared/http/operators';
import { buildApiEndpoint, buildHttpContext } from '@core/http';

@Injectable({
  providedIn: 'root',
})
export class AuthRepository {
  private readonly http = inject(HttpClient);

  login(payload: LoginPayload): Observable<Credentials> {
    return this.http
      .post<ApiResponse<CredentialsDto>>(buildApiEndpoint('auth.login'), payload, {
        context: buildHttpContext({ skipAuth: true, skipAuthRefresh: true }),
      })
      .pipe(mapDataNonNull(mapCredentialsFromDto));
  }

  logout(): Observable<void> {
    return this.http.post<void>(buildApiEndpoint('auth.logout'), null, {
      context: buildHttpContext({ skipAuthRefresh: true }),
    });
  }

  refreshToken(): Observable<Credentials> {
    return this.http
      .post<ApiResponse<CredentialsDto>>(buildApiEndpoint('auth.refreshToken'), null, {
        context: buildHttpContext({ skipAuthRefresh: true }),
      })
      .pipe(mapDataNonNull(mapCredentialsFromDto));
  }

  getRegistrationInfo(confirmationCode: string): Observable<RegistrationInfo> {
    return this.http
      .get<ApiResponse<RegistrationInfoDto>>(buildApiEndpoint('auth.setup', { confirmationCode }), {
        context: buildHttpContext({ skipAuth: true, skipAuthRefresh: true }),
      })
      .pipe(mapDataNonNull(mapRegistrationInfoFromDto));
  }

  initPasswordReset(payload: InitPasswordResetPayload): Observable<void> {
    return this.http.post<void>(buildApiEndpoint('auth.passwordResetInit'), payload, {
      context: buildHttpContext({ skipAuth: true, skipAuthRefresh: true }),
    });
  }

  completePasswordReset(payload: CompletePasswordResetPayload): Observable<void> {
    return this.http.post<void>(
      buildApiEndpoint('auth.passwordResetComplete'),
      mapCompletePasswordResetPayloadToDto(payload),
      {
        context: buildHttpContext({ skipAuth: true, skipAuthRefresh: true }),
      },
    );
  }

  completeRegistration(payload: CompleteRegistrationPayload): Observable<Credentials> {
    return this.http
      .post<ApiResponse<CredentialsDto>>(
        buildApiEndpoint('auth.setupComplete'),
        mapCompleteRegistrationPayloadToDto(payload),
        {
          context: buildHttpContext({ skipAuth: true, skipAuthRefresh: true }),
        },
      )
      .pipe(mapDataNonNull(mapCredentialsFromDto));
  }
}
